import type { ClientMediaItem } from '@DigitaleDoerfer/digitale-doerfer-api';
import { Image } from 'expo-image';
import { useEffect, useMemo, useState, type FC } from 'react';
import type { ColorValue, ImageStyle, StyleProp } from 'react-native';
import { StyleSheet, View } from 'react-native';
import type { SurfaceProps } from 'react-native-paper';
import {
  ActivityIndicator,
  Surface,
  TouchableRipple
} from 'react-native-paper';

import { BrokenImageIcon } from '@/assets';
import { THUMB_HASH } from '@/features/media/constants/image';
import type { MediaItemSize } from '@/features/media/types/mediaItem';
import { getSizedMediaItemUrl } from '@/features/media/utils/mediaItem';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    loadingWrapper: {
      backgroundColor: theme.colors.surfaceContainerLow,
      alignItems: 'center',
      justifyContent: 'center'
    },
    fallbackWrapper: {
      backgroundColor: theme.colors.surfaceVariant,
      alignItems: 'center',
      justifyContent: 'center'
    }
  });

export type MediaItemImageProps = {
  isLoading?: boolean;
  mediaItem?: ClientMediaItem;
  mediaSize?: MediaItemSize;
  localImageUri?: string;
  style?: StyleProp<ImageStyle>;
  pressedColor?: ColorValue;
  elevation?: SurfaceProps['elevation'];
  onPress?: () => void;
  onLongPress?: () => void;
};

export const MediaItemImage: FC<MediaItemImageProps> = ({
  isLoading = false,
  mediaItem,
  mediaSize,
  localImageUri,
  style,
  pressedColor,
  elevation = 0,
  onPress,
  onLongPress
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const sizedUrl = getSizedMediaItemUrl(mediaItem, mediaSize)?.url;
  const imageSource = localImageUri ?? sizedUrl;

  const [hasLoadError, setHasLoadError] = useState(false);

  // Reset the error flag whenever the source changes so a new (valid) image
  // isn't stuck on the fallback from a previous failed one.
  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    setHasLoadError(false);
  }, [imageSource]);

  if (isLoading) {
    return (
      <Surface elevation={elevation} style={style}>
        <View style={[style, styles.loadingWrapper]}>
          <ActivityIndicator size="large" />
        </View>
      </Surface>
    );
  }

  if (isEmptyOrNull(imageSource)) return;

  if (hasLoadError) {
    return (
      <Surface elevation={elevation} style={style}>
        <View style={[style, styles.fallbackWrapper]}>
          <BrokenImageIcon
            fill={theme.colors.onSurfaceVariant}
            width={32}
            height={32}
          />
        </View>
      </Surface>
    );
  }

  const content = (
    <Image
      source={{ uri: imageSource }}
      placeholder={{ thumbhash: mediaItem?.thumbHash ?? THUMB_HASH }}
      style={style}
      contentFit="cover"
      placeholderContentFit="fill"
      cachePolicy="disk"
      recyclingKey={imageSource}
      onError={() => setHasLoadError(true)}
      accessibilityIgnoresInvertColors
      draggable={false}
    />
  );

  const isClickable = Boolean(onPress || onLongPress);

  return (
    <Surface elevation={elevation} style={style}>
      {isClickable ? (
        <TouchableRipple
          onPress={onPress}
          onLongPress={onLongPress}
          rippleColor={pressedColor}
          style={style}
        >
          {content}
        </TouchableRipple>
      ) : (
        content
      )}
    </Surface>
  );
};
